import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';

import BooksEventItemList from '../../../../../molecules/merchant/resources/book/BooksEventItemList';
import LabeledContainer from '../../../../../molecules/common/LabeledContainer';
import BooksNotes from '../../../../../molecules/merchant/resources/book/BooksNotes';
import CheckInput, { InputType } from '../../../../../atoms/CheckInput';
import BooksConfirmAlert from '../../../../../molecules/merchant/resources/book/BooksConfirmAlert';
import BooksTermsOfUse from '../../../../../molecules/merchant/resources/book/BooksTermsOfUse';
import { SettingConfirmation } from '../../../../../interfaces/merchants/resources/books';
import { useResourceNotes } from '../../../../../helpers/merchants/resources/helper';
import { scrollToTop } from '../../../../../hooks/useScrollToTop';

import {
  inputConfirmation,
  ReinputPaymentError,
  ReselectDateError,
  RestartReservationError,
} from '../../../../../redux/actions/merchants/resources/books/inputs';
import { useLineLiffAccessToken } from '../../../../../hooks/line-liff';
import css from './style.module.scss';
import { useAppDispatch } from '../../../../../redux';
import { RSButton } from 'src/reservation-page/RSButton';
import { buildSiteUrl } from 'src/utils/request-url';

interface ElementProps {
  merchantResource: { publicId: string; name: string; imageUrl?: string };
  settings: SettingConfirmation;
  postable: boolean;
  onSubmit: () => void;
  onReinputPayment: () => void;
  onReselect: () => void;
  onRestart: () => void;
  publicId: string;
  lineLiffId: string | undefined;
}

type Props = ElementProps;

const InputConfirmationEvent: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const {
    merchantResource,
    postable,
    settings,
    onSubmit,
    onReinputPayment,
    onReselect,
    onRestart,
    settings: { selectedPaymentMethod },
    publicId,
    lineLiffId,
  } = props;
  const intl = useIntl();
  const notes = useResourceNotes(settings);

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();

  const [isTermAccepted, setIsTermAccepted] = useState<boolean | undefined>(
    settings.resourceTerm ? false : undefined,
  );
  const { lineAccessToken, lineIdToken } = useLineLiffAccessToken(lineLiffId);

  useEffect(() => {
    scrollToTop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheck = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.currentTarget;
    setIsTermAccepted(checked);
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      await dispatch(
        inputConfirmation({
          reservationId: inputs.reservationId!,
          termsAccepted: isTermAccepted,
          selectedPaymentMethod,
          lineAccessToken,
          lineIdToken,
          merchantPublicId: publicId,
        }),
      );
      onSubmit();
    } catch (e) {
      if (e instanceof ReselectDateError) {
        onReselect();
      } else if (e instanceof RestartReservationError) {
        onRestart();
      } else if (e instanceof ReinputPaymentError) {
        onReinputPayment();
      }
    }
  }, [
    dispatch,
    isTermAccepted,
    inputs,
    selectedPaymentMethod,
    onSubmit,
    onReselect,
    onRestart,
    onReinputPayment,
    lineAccessToken,
    lineIdToken,
    publicId,
  ]);

  const reservations = inputs;

  return (
    <div>
      <BooksConfirmAlert
        termsUrl="https://stores.fun/legal/reserve-user-terms"
        lawUrl={buildSiteUrl(`/legal_notice`)}
      />
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.booksItems',
        })}
        paddingTop={true}
      >
        <BooksEventItemList
          nickname={props.publicId}
          resourceId={merchantResource.publicId}
          resourceName={merchantResource.name}
          resourceImage={merchantResource.imageUrl ?? ''}
          price={
            reservations.payment &&
            reservations.payment.paymentMethod.method !== 'subscription' &&
            reservations.payment.paymentMethod.method !== 'ticket' &&
            reservations.payment.paymentMethod.method !==
              'first_time_free_voucher'
              ? reservations.payment.paymentMethod.price
              : undefined
          }
          payment={
            reservations.payment && reservations.payment.paymentMethod.method
          }
          eventTimeSlot={reservations.eventTimeSlot!}
          attendees={reservations.eventTimeSlot!.attendees}
        />
      </LabeledContainer>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.attentionForBooking',
        })}
        contentsPadding={true}
        paddingTop={true}
      >
        <BooksNotes notes={notes} />
      </LabeledContainer>
      {settings.resourceTerm && (
        <>
          <LabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.termsOfService',
            })}
            contentsPadding={true}
            paddingTop={true}
          >
            <BooksTermsOfUse text={settings.resourceTerm} />
          </LabeledContainer>
          <LabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.needAgree',
            })}
          >
            <CheckInput
              type={InputType.Checkbox}
              // @ts-expect-error strictFunctionTypes
              onChange={handleCheck}
              name="terms-of-use"
              value="agree"
            >
              {intl.formatMessage({
                id: 'books.forms.agreeTermsOfService',
              })}
            </CheckInput>
          </LabeledContainer>
        </>
      )}
      <div className={css.submit_button}>
        <RSButton
          variant="basic"
          isBlock
          isLoading={!postable}
          disabled={(!!settings.resourceTerm && !isTermAccepted) || !postable}
          value={intl.formatMessage({
            id: 'books.submitLabel.reserve',
          })}
          onClick={handleSubmit}
        >
          {intl.formatMessage({
            id: 'books.submitLabel.reserve',
          })}
        </RSButton>
      </div>
    </div>
  );
};

export default InputConfirmationEvent;
